import { EMPTY_STRING } from '@rotomeca/utils';

type Style =
	| string[]
	| string
	| CSSStyleSheet
	| CSSStyleSheet[]
	| (string | CSSStyleSheet)[];

/**
 * Options de configuration pour le décorateur `@Define`.
 */
export interface DefineOptions {
	/**
	 * Le nom du tag HTML du Web Component (ex: 'bnum-button').
	 * Si défini, il créera ou écrasera automatiquement la propriété statique `TAG` de la classe.
	 */
	tag?: string;

	/**
	 * La vue JSX ou la chaîne HTML du composant.
	 * Sera compilée en `HTMLTemplateElement` une seule fois à l'initialisation de la classe.
	 */
	template?: string | Node;

	/**
	 * Les styles CSS du composant.
	 * Seront compilés - ou non - en `CSSStyleSheet` une seule fois à l'initialisation de la classe.
	 */
	styles?: Style;
}

/**
 * Décorateur de classe pour définir un Web Component.
 * * Il gère automatiquement :
 * 1. L'enregistrement du Custom Element via `customElements.define`.
 * 2. La création et la mise en cache du Template (Performance).
 * 3. La création et la mise en cache des Styles (Performance).
 * 4. La définition de la propriété statique `TAG` sur la classe.
 *
 * @param options Les options de configuration (tag, template, style)
 * @example
 * ```tsx
 * const VIEW = <div class="box"><slot /></div>;
 * @Define({
 * tag: 'my-component',
 * template: VIEW,
 * styles: '.box { color: red; }'
 * })
 * export class MyComponent extends BnumElementInternal {
 * // Pas besoin de déclarer static get TAG(), c'est automatique !
 * }
 * ```
 */
export function Define(options: DefineOptions = {}) {
	return function (target: Function, context: ClassDecoratorContext) {
		// Vérification de sécurité : on ne décore que des classes
		if (context.kind !== 'class') {
			throw new Error('@Define ne peut être utilisé que sur une classe.');
		}

		// Initialisation unique au chargement de la classe (Load Time)
		context.addInitializer(function () {
			const clazz = this as any;

			// ---------------------------------------------------------
			// 1. INJECTION DU STATIC TAG
			// ---------------------------------------------------------
			if (options.tag) {
				// On définit ou redéfinit la propriété statique 'TAG'
				Object.defineProperty(clazz, 'TAG', {
					get: () => options.tag,
					configurable: true, // Permet d'être reconfiguré si nécessaire
					enumerable: true,
				});
			}

			// Vérification finale pour s'assurer qu'on a un TAG valide
			const finalTag = clazz.TAG;
			if (!finalTag) {
				console.warn(
					`[Define] La classe ${context.name} n'a pas de TAG défini (ni via options, ni via static TAG).`,
				);
				return; // On ne peut pas enregistrer sans tag
			}

			// ---------------------------------------------------------
			// 2. COMPILATION UNIQUE DU TEMPLATE (CACHE)
			// ---------------------------------------------------------
			if (options.template) {
				const tpl = document.createElement('template');

				if (options.template instanceof Node) {
					// Support du JSX DOM Node
					tpl.content.appendChild(options.template);
				} else {
					// Support du JSX String ou HTML String
					tpl.innerHTML = String(options.template);
				}

				// Stockage caché sur le constructeur
				clazz.__CACHE_TEMPLATE__ = tpl;
			}

			// ---------------------------------------------------------
			// 3. COMPILATION UNIQUE DES STYLES (CACHE)
			// ---------------------------------------------------------
			if (options.styles) initStyle(clazz, options.styles);

			// ---------------------------------------------------------
			// 4. ENREGISTREMENT DU WEB COMPONENT
			// ---------------------------------------------------------
			if (!customElements.get(finalTag)) {
				customElements.define(finalTag, clazz);
			}
		});
	};
}

/**
 * Fonction qui gère le style de la classe
 *
 * @remark
 * Prend en compte les tableaux de string et les tableaux de CSSStyleSheet, mais aussi ces types la, à l'unitée.
 *
 * Voir {@link Style}
 *
 * @param clazz Classe qui contiendra le cache
 * @param styles Styles à ajouter au cache
 * @internal
 */
function initStyle(clazz: { __CACHE_STYLE__: CSSStyleSheet[] }, styles: Style) {
	let strStyles;
	const array = Array.isArray(styles) ? styles : [styles];
	clazz.__CACHE_STYLE__ = [];

	for (const style of array) {
		if (style instanceof CSSStyleSheet) clazz.__CACHE_STYLE__.push(style);
		else if (!strStyles) strStyles = style;
		else strStyles += style;
	}

	if (strStyles && strStyles !== EMPTY_STRING) {
		const sheet = new CSSStyleSheet();
		sheet.replaceSync(strStyles);
		clazz.__CACHE_STYLE__.push(sheet);
	}
}
